/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { gql, useLazyQuery } from '@apollo/client'
import { faDoorClosed, faFingerprint, faLock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Cookies from 'js-cookie'
import { uniqueId } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useRef, type FC } from 'react'
import { Badge } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Nav from 'react-bootstrap/Nav'
import NavDropdown from 'react-bootstrap/NavDropdown'
import NavbarBase from 'react-bootstrap/Navbar'
import Countdown from 'react-countdown'
import { Initial } from 'react-initial'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import type { Query } from '../../gql/graphql'
import { MenuItem } from '../DashboardLayout'
import TogglePasswordModal from '../TogglePasswordModal'

const Navbar = styled(NavbarBase)`
  border: 0px;
  border-color: transparent;
  /* box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 4px 1px; */
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  & ~ *:not(.header) {
    margin-top: calc(var(--app-navbar-height, 90px) + 16px);
  }
`

const QUERY_CURRENT_USER = gql`
  query($isAuthenticated: Boolean!) {
    currentUser @include(if: $isAuthenticated) {
      name
      role
    }
  }
`

export type Props = {
  isAuthenticated?: boolean,
  routes?: MenuItem[]
}

/**
 * NavigationBar is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import NavigationBar from './src/components/NavigationBar/NavigationBar.tsx'
 *
 * const MyComponent = () => (
 *   <NavigationBar />
 * )
 * export default MyComponent
 * ```
 */
const NavigationBar: FC<Props> = ({ isAuthenticated = true, routes = [] }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const navbarRef = useRef<HTMLElement>(null)
  const [currentUser, { data }] = useLazyQuery<Query>(QUERY_CURRENT_USER, {
    fetchPolicy: 'network-only',
    variables: {
      isAuthenticated
    }
  })

  const currentRoutes = useMemo(() => {
    return routes.map(area => {
      return {
        ...area,
        routes: area?.routes?.map(route => ({
          isActive: route.path === '/'
            ? pathname === route.path
            : pathname.startsWith(route.path),
          ...route
        }))
      }
    })
  }, [routes, pathname])

  useEffect(() => {
    if (isAuthenticated) {
      currentUser()
    }
  }, [isAuthenticated, currentUser])

  useEffect(() => {
    const element = navbarRef.current
    if (element == null) {
      return
    }

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--app-navbar-height',
        `${Math.ceil(element.getBoundingClientRect().height)}px`
      )
    }

    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <Navbar ref={navbarRef} expand='lg' fixed='top' bg="light" data-bs-theme="light">
      <Container>
        <Navbar.Brand as={Link} href='/' to='/'>
          <img
            src={process.env.PUBLIC_URL + '/logo-mundial.png'}
            height={30}
            className='d-inline-block align-top'
            alt='Supermercados Mundial logo'
            style={{ marginLeft: 10 }}
          />
          {' '}
          <strong style={{ color: theme.colors.primary, fontSize: 13, fontWeight: 600 }}>
            Backoffice
          </strong>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        {data?.currentUser?.name && (
          <>
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                <Nav.Link disabled href="#home">
                  <Countdown
                    onComplete={() => navigate('/logout')}
                    date={moment(Cookies.get('token_expires_at')).toDate()}
                    renderer={({ hours, minutes, seconds }) => (
                      <small>Fim da sessão em {hours}:{minutes}:{seconds}</small>
                    )}
                  />
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
            <Navbar.Collapse className='justify-content-end'>
              <Nav>
                {currentRoutes.map(area => {
                  const visibleAreas = area?.routes?.filter(route => route.when !== false) ?? []

                  if (area.when === false || (Array.isArray(area?.routes) && visibleAreas.length === 0)) {
                    return null
                  }

                  if (area.path) {
                    return (
                      <Nav.Link
                        key={area.title}
                        // as={Button}
                        active={!area.disabled}
                        onClick={() => navigate(area.path as string)}
                      >
                        {area?.focusedIcon && <FontAwesomeIcon icon={area.focusedIcon} />}
                        {' '}
                        {area.title}
                      </Nav.Link>
                    )
                  }

                  return (
                    <NavDropdown
                      key={area?.title ?? uniqueId()}
                      title={(
                        <>
                          {area?.focusedIcon && <FontAwesomeIcon icon={area.focusedIcon} />}
                          {' '}
                          {area?.title}
                        </>
                      )}
                      id={(area?.title ?? uniqueId()).toLowerCase().replace(/\s/g, '-')}
                    >
                      {area?.routes?.map(route => {
                        if (route.when === false) {
                          return null
                        }

                        return (
                          <NavDropdown.Item
                            key={route.title}
                            as={Button}
                            disabled={route.disabled}
                            active={route.isActive}
                            onClick={() => navigate(route.path)}
                          >
                            <FontAwesomeIcon icon={route.isActive ? route.focusedIcon : route.unfocusedIcon ?? route.focusedIcon} />
                            {' '}
                            {route.title}
                            {route.badge && (
                              <small>
                                {' '}
                                <Badge bg="secondary" pill>{route.badge}</Badge>
                              </small>
                            )}
                          </NavDropdown.Item>
                        )
                      })}
                    </NavDropdown>
                  )
                })}
                <NavDropdown
                  title={(
                    <Initial
                      name={data?.currentUser?.name}
                      radius={200}
                      fontSize={15}
                      width={25}
                      height={25}
                      color={theme.colors.primary}
                      style={{ borderRadius: 1000 }}
                    />
                  )}
                >
                  <TogglePasswordModal
                    component={props => (
                      <NavDropdown.Item as={Button} onClick={props.onClick}>
                        <FontAwesomeIcon icon={faLock} />
                        {' '}
                        Alterar minha senha
                      </NavDropdown.Item>
                    )}
                  />
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Button} onClick={() => navigate('/passkeys')}>
                    <FontAwesomeIcon icon={faFingerprint} />
                    {' '}
                    Chaves de acesso
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Button} onClick={() => navigate('/logout')}>
                    <FontAwesomeIcon icon={faDoorClosed} />
                    {' '}
                    Encerrar sessão
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </Navbar.Collapse>
          </>
        )}
      </Container>
    </Navbar>
  )
}

export default NavigationBar
